
import { CalendarDateTime, DateFormatter, DateValue, getLocalTimeZone } from "@internationalized/date"
import {
    PublicationBlockAuthorInput,
    PublicationBlockBrissagoInput,
    PublicationBlockBrissagoPrivate,
    PublicationBlockCarouselImageInput,
    PublicationBlockCarouselInput,
    PublicationBlockCarouselPrivate,
    PublicationBlockGalleryInput,
    PublicationBlockGalleryPrivate,
    PublicationBlockImageInput,
    PublicationBlockImagePrivate,
    PublicationBlockLinkInput,
    PublicationBlockPrivate,
    PublicationBlockTextInput,
    PublicationBlockVideoInput,
    PublicationBlockVideoPrivate,
    PublicationCreate,
    PublicationUpdate,
    PublicationsOrderBy,
    PublicationsService,
    SettingsCommonService
} from "../client"


/* Interfaces */
export interface PublicationsTableProps {
    pu_create: boolean
    pu_update: boolean
    pu_delete: boolean
}

export interface DetailPublicationProps {
    onClose: () => void
    isOpen: boolean
    publicationId: string
}

export interface AddPublicationProps {
    onClose: () => void
    isOpen: boolean
}

export interface PublicationBlockVideoInputExt extends PublicationBlockVideoInput {
  file?: File | null
  file_path?: string | null
}

export interface PublicationBlockBrissagoInputExt extends PublicationBlockBrissagoInput {
  file?: File | null
  file_path?: string | null
}

export interface PublicationBlockImageInputExt extends PublicationBlockImageInput {
  file?: File | null
  file_path?: string | null
}

export type PublicationBlockGalleryInputFile = {
  file?: File | null
  fk_media_id?: string
}

export interface PublicationBlockGalleryInputExt extends PublicationBlockGalleryInput {
  files?: PublicationBlockGalleryInputFile[] | null
}

export interface PublicationBlockCarouselImageInputExt extends PublicationBlockCarouselImageInput {
  file?: File | null
  file_path?: string | null
}

export interface PublicationBlockCarouselInputExt extends PublicationBlockCarouselInput {
  fk_carousel_images: Array<PublicationBlockCarouselImageInputExt>
}

export interface PublicationCreateExt extends PublicationCreate {
  fk_blocks?: Array<
    | PublicationBlockAuthorInput
    | PublicationBlockTextInput
    | PublicationBlockVideoInputExt
    | PublicationBlockBrissagoInputExt
    | PublicationBlockImageInputExt
    | PublicationBlockGalleryInputExt
    | PublicationBlockLinkInput
    | PublicationBlockCarouselInputExt
  > | null
}

export interface PublicationUpdateExt extends PublicationUpdate {
  fk_blocks?: Array<
    | PublicationBlockAuthorInput
    | PublicationBlockTextInput
    | PublicationBlockVideoInputExt
    | PublicationBlockBrissagoInputExt
    | PublicationBlockImageInputExt
    | PublicationBlockGalleryInputExt
    | PublicationBlockLinkInput
    | PublicationBlockCarouselInputExt
  > | null
}


/* Endpoints */
export const readPublications = ({
    per_page,
    page,
    order_by,
    descending,
    query,
    showOnlyHidden,
}: {
    per_page: number,
    page: number
    order_by?: PublicationsOrderBy
    descending?: boolean
    query?: string,
    showOnlyHidden?: boolean,
}) => {
    return {
        queryFn: async () => {
        const data = await PublicationsService.readPublications({
            skip: (page - 1) * per_page,
            limit: per_page,
            orderBy: order_by,
            descending: descending,
            query: query,
            showOnlyHidden: showOnlyHidden,
        })

        return data ?? { data: null, count: 0 }
        },
        queryKey: ["publications", { page, order_by, descending, query, showOnlyHidden }],
    }
}

export const readPublicationById = (publicationId: string) => {
  return {
    queryFn: async () => {
      const data =  await PublicationsService.readPublicationById({
        publicationId: publicationId,
      })
      return data ?? { data: null, count: 0 }
    },
    queryKey: ["publication", { publicationId }],
  }
}

export const readNextPublishDt = () => {
    return {
        queryFn: async () => {
            const data =  await SettingsCommonService.readNextPublishDt()
            return data ?? { data: null, count: 0 }
        },
        queryKey: ["next-publishdate"],
    }
}


/* Functions */
export function generateSeoTitle(title: string) {
    return title
        .trim()
        .toLowerCase()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/[^a-z0-9\s-]/g, "")   // remove remaining special chars
        .replace(/\s+/g, "-")   // spaces → hyphens
        .replace(/-+/g, "-")    // collapse multiple hyphens
}

export const formatter = new DateFormatter("de-CH", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
})

export const formatterLongDate = new DateFormatter("de-CH", {
    day: "numeric",
    month: "long",
    year: "numeric",
})

export function formatTime(dtValue: CalendarDateTime[] | []) {
  if (!dtValue[0]) return ""
  const h = String(dtValue[0].hour).padStart(2, "0")
  const m = String(dtValue[0].minute).padStart(2, "0")
  return `${h}:${m}`
}

export function handleTimeChange(
  e: React.ChangeEvent<HTMLInputElement>,
  dtValue: CalendarDateTime[],
  setDtValue: any,
  setTimeValue: any,
  onChange: (v: string) => void
) {
  const [hours, minutes] = e.currentTarget.value.split(":").map(Number)

  // Use the existing date from dtValue
  const base = dtValue[0] ?? new CalendarDateTime(2025, 1, 1, 0, 0)

  const updated = base.set({ hour: hours, minute: minutes })

  setDtValue([updated])
  setTimeValue(formatTime([updated]))
  onChange(updated.toDate(getLocalTimeZone()).toISOString())
}

export function handleDateChange(
  details: { value: DateValue[] },
  dtValue: CalendarDateTime[],
  setDtValue: any,
  setTimeValue: any,
  onChange: (v: string) => void
) {
  const newDate = details.value[0]

  if (!newDate) {
    setDtValue([])
    setTimeValue("")
    onChange("")
    return
  }

  const prevTime = dtValue[0] ?? { hour: 0, minute: 0 }

  const updated = new CalendarDateTime(
    newDate.year,
    newDate.month,
    newDate.day,
    prevTime.hour,
    prevTime.minute
  )

  setDtValue([updated])
  setTimeValue(formatTime([updated]))
  onChange(updated.toDate(getLocalTimeZone()).toISOString())
}

export function mapBlockToInput(
  block: PublicationBlockPrivate
): NonNullable<PublicationUpdateExt["fk_blocks"]>[number] {
  const block_type = block.block_type
  const data = block.block_data

  switch (block.block_type) {
    case "author":
        return {block_type, ...data} as PublicationBlockAuthorInput
    case "text":
        return {block_type, ...data} as PublicationBlockTextInput
    case "video": {
      const data = block.block_data as PublicationBlockVideoPrivate
    
      return {
        block_type: "video",
        fk_media_id: data?.fk_media?.id,
        description: data?.description ?? null,
        url: data?.url,
        file: null,
        file_path: data?.fk_media?.path,
      } satisfies PublicationBlockVideoInputExt
    }
    case "brissago": {
        const data = block.block_data as PublicationBlockBrissagoPrivate

        return {
            block_type: "brissago",
            fk_media_id: data?.fk_media.id,
            description: data?.description ?? null,
            file: null,
            file_path: data?.fk_media.path,
        } satisfies PublicationBlockBrissagoInputExt
    }
    case "image": {
        const data = block.block_data as PublicationBlockImagePrivate

        return {
            block_type: "image",
            caption: data?.caption ?? null,
            is_archive_image: data?.is_archive_image,
            fk_media_id: data?.fk_media.id,
            fk_author_id: data?.fk_author?.id ?? "",
            file: null,
            file_path: data?.fk_media.path,
        } satisfies PublicationBlockImageInputExt
    }
    case "gallery": {
        const data = block.block_data as PublicationBlockGalleryPrivate

        return {
            block_type: "gallery",
            fk_gallery_images: data?.fk_gallery_images?.map(img => ({
              fk_media_id: img.fk_media.id
            })),
            fk_author_ids: data?.fk_authors?.map(a => a.id) ?? [],
            files: data?.fk_gallery_images?.map(img => ({
              file: null,
              fk_media_id: img.fk_media.id
            })),
        } satisfies PublicationBlockGalleryInputExt
    }
    case "link":
        return {block_type, ...data} as PublicationBlockLinkInput
    case "carousel": {
        const data = block.block_data as PublicationBlockCarouselPrivate

        return {
            block_type: "carousel",
            fk_carousel_images: data?.fk_carousel_images?.map(img => ({
                caption: img.caption,
                fk_media_id: img.fk_media.id,
                fk_author_id: img.fk_author?.id ?? "",
                file: null,
                file_path: img.fk_media.path,
            })),
        } satisfies PublicationBlockCarouselInputExt
    }
  }
}
